import { MoreVert } from "@mui/icons-material";
import { Box, Typography, Chip, useTheme, IconButton, Menu, MenuItem, Checkbox, ListItemText } from "@mui/material";
import { CaseResponseDto, PagedResponse } from "@bandido/shared/dtos";
import { useState } from "react";
import { addLeadingZeros } from "@frontend/features/common/utils/helpers";
import { defaultLeadingZeros } from "@frontend/features/common/constants";
import { useWhatsappClient } from "../WhatsappClientContext";
import { useTranslation } from "react-i18next";
import { MessageResponse } from "../../types";
import { ChatMessage } from "../ChatMessage";
import { ChatSticker } from "../ChatSticker";
import { ChatAudio } from "../ChatAudio";
import { ChatImage } from "../ChatImage";
import { ChatVideo } from "../ChatVideo";
import { ChatContact } from "../ChatContact";
import { ChatDocument } from "../ChatDocument";
import { ChatLocation } from "../ChatLocation";

interface Props {
  casesData?: PagedResponse<CaseResponseDto>;
  whatsappMsgId?: string;
  children: React.ReactNode;
  backgroundColor?: string;
  textColor?: string;
}

type ChatBaseProps = MessageResponse & Props

export const ChatBase: React.FC<ChatBaseProps> = (data) => {
  const { createdAt, inbound, casesData, cases, whatsappMsgId, children, backgroundColor, textColor, status, contextMessage } = data;
  const time = new Date(createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const theme = useTheme();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { handleLinkMessageToCase, setMessageToReply } = useWhatsappClient();

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const onCaseToggle = async (caseId: number) => {
    if (whatsappMsgId) {
      await handleLinkMessageToCase(whatsappMsgId, caseId);
    }
  };

  const handleReplyMessage = () => {
    if (!data) return;
    setMessageToReply(data);
    setAnchorEl(null);
  };

  const renderContextMessage = () => {
    if (!contextMessage) return null;

    const sender =
      contextMessage.senderName === "BANDIDO_SYSTEM" ? t('whatsapp.systemSender') : contextMessage.senderName;

    const preview = (() => {
      const msg = contextMessage;
      switch (msg.type) {
        case 'text':
          return <ChatMessage message={msg.text || ''} />;
        case 'contacts':
          return <ChatContact message={msg.text || ''} />;
        case 'template':
          if (msg.caption) {
            switch (msg.caption) {
              case 'image':
                return msg.text ? <ChatImage url={msg.text} size="compact" /> : null;
              case 'video':
                return msg.text ? <ChatVideo fileId={msg.text} size="compact" /> : null;
              case 'audio':
                return msg.text ? <ChatAudio fileId={msg.text} size="compact" /> : null;
              case 'document':
                return msg.text ? (
                  <ChatDocument
                    fileId={msg.text}
                    filename={msg.filename || t('common.view')}
                    size="compact"
                  />
                ) : null;
              default:
                return <ChatMessage message={msg.text || ''} />;
            }
          }
          return <ChatMessage message={msg.text || ''} />;
        case 'sticker':
          return msg.text ? <ChatSticker url={msg.text} size="compact" /> : null;
        case 'audio':
          return msg.text ? <ChatAudio fileId={msg.text} size="compact" /> : null;
        case 'video':
          return msg.text ? <ChatVideo fileId={msg.text} size="compact" /> : null;
        case 'image':
          return msg.text ? <ChatImage url={msg.text} caption={msg.caption || undefined} size="compact" /> : null;
        case 'document':
          return msg.text ? (
            <ChatDocument
              fileId={msg.text}
              caption={msg.caption || undefined}
              filename={msg.filename || t('common.view')}
              size="compact"
            />
          ) : null;
        case 'location':
          return msg.text ? <ChatLocation latLong={msg.text} size="compact" /> : null;
        default:
          return null;
      }
    })();

    return (
      <Box
        mb={1}
        p={1.5}
        borderRadius={1}
        bgcolor={"rgba(255, 255, 255, 0.8)"}
        borderLeft={`3px solid ${theme.palette.primary.main}`}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {sender}
        </Typography>
        <Box sx={{ mt: 0.5 }}>
          {preview}
        </Box>
      </Box>
    );
  };

  return (
    <Box display="flex" justifyContent={inbound ? "flex-start" : "flex-end"} mb={1}>
      <Box textAlign={inbound ? "left" : "right"} maxWidth="100%">
        <Box
          display="flex"
          alignItems="start"
          p={2}
          mr={1}
          gap={1}
          borderRadius={2}
          bgcolor={inbound ? "common.white" : (backgroundColor || theme.palette.primary.states?.focus)}
          color={inbound ? "text.primary" : (textColor || "text.primary")}
          sx={{
            borderTopLeftRadius: inbound ? 0 : 2,
            borderTopRightRadius: inbound ? 1 : 0,
          }}
        >
          <Box
            sx={{
              wordBreak: 'break-word',
              textWrap: 'auto',
              whiteSpace: 'balance',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              gap: 1,
              flexDirection: 'column',
              alignItems: 'flex-start',
              display: 'flex',
            }}
          >
            <Box display="flex" flexDirection="row" gap={1} justifyContent="space-between" width="100%">
              <Box display="flex" flexDirection="row" gap={1}>
                {cases?.map(item => (
                  <Chip 
                    size="small"
                    key={item} 
                    color="secondary" 
                    label={addLeadingZeros(item, defaultLeadingZeros)} 
                  />

                ))}
              </Box>
              <Box display="flex" alignItems="center">
                <IconButton
                  onClick={handleMenuClick}
                  color={inbound ? "default" : "inherit"}
                  size="small"
                  >
                  <MoreVert sx={{ fontSize: 18 }} />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  <MenuItem onClick={handleReplyMessage}>
                    Responder a este mensaje
                  </MenuItem>
                  {casesData && casesData.items.length > 0 && (
                    <MenuItem>
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Casos linkeados</Typography>
                        {casesData.items.map((caseItem: CaseResponseDto) => (
                          <Box key={caseItem.id} sx={{ display: 'flex', alignItems: 'center' }}>
                            <Checkbox
                              checked={cases?.includes(caseItem.id)}
                              onChange={() => onCaseToggle(caseItem.id)}
                              disabled={cases?.includes(caseItem.id)}
                            />
                            <ListItemText primary={`#${caseItem.id} - ${caseItem.name}`} />
                          </Box>
                        ))}
                      </Box>
                    </MenuItem>
                  )}
                </Menu>
              </Box>
            </Box>
            {renderContextMessage()}
            {children}
          </Box>
        </Box>

        <Box px={2} display="flex" flexDirection="row-reverse">
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            {!inbound && status && `${t(`whatsapp.messageStatus.${status}` as const)} · `}{time}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}; 